import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { sec } from "../../../security";
import { addNotification } from "../../notifications/notificationsSlice";
import {
  AddExcludedFirmRequest,
  AddExcludedFirmResponse,
  ExcludedFirm,
  ExcludedFirmsResponse
} from "./model/excludedFirm";

const apiServerUrl = process.env.REACT_APP_HAISTACK_API_URL;

export const excludedFirmsApi = createApi({
  reducerPath: "excludedFirmsV2",
  tagTypes: ["ExcludedFirms"],
  baseQuery: fetchBaseQuery({
    baseUrl: apiServerUrl,
    prepareHeaders: async (headers) => {
      const token = await sec.getAccessTokenSilently()();
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    }
  }),
  endpoints: (builder) => ({
    getExcludedFirms: builder.query<ExcludedFirm[], void>({
      query: () => "/api/v2/excluded_firms",
      transformResponse: (response: ExcludedFirmsResponse) =>
        response.excluded_firms,
      providesTags: ["ExcludedFirms"]
    }),
    addExcludedFirm: builder.mutation<ExcludedFirm, AddExcludedFirmRequest>({
      query: (body) => ({
        url: "/api/v2/excluded_firms",
        method: "POST",
        body
      }),
      transformResponse: (response: AddExcludedFirmResponse) =>
        response.excluded_firm,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: "Firm excluded successfully",
              type: "success"
            })
          );
        } catch (error: any) {
          const errorMessage = error?.data?.message || "Failed to exclude firm";
          dispatch(
            addNotification({
              message: errorMessage,
              type: "danger"
            })
          );
        }
      },
      invalidatesTags: ["ExcludedFirms"]
    }),
    removeExcludedFirm: builder.mutation<void, number>({
      query: (employerId) => ({
        url: `/api/v2/excluded_firms/${employerId}`,
        method: "DELETE"
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: "Firm removed from exclusions successfully",
              type: "success"
            })
          );
        } catch (error: any) {
          const errorMessage =
            error?.data?.message || "Failed to remove firm exclusion";
          dispatch(
            addNotification({
              message: errorMessage,
              type: "danger"
            })
          );
        }
      },
      invalidatesTags: ["ExcludedFirms"]
    })
  })
});

// Export hooks for usage in functional components
export const {
  useGetExcludedFirmsQuery,
  useAddExcludedFirmMutation,
  useRemoveExcludedFirmMutation
} = excludedFirmsApi;
